import { createRouter, createWebHistory } from '@ionic/vue-router';
import { NavigationGuard, Router, RouteRecordRaw } from 'vue-router';
import HomePage from '../views/HomePage.vue';
import { useAuth } from '@/stores/auth.store';
import LoginPage from '@/views/LoginPage.vue';
import MapPage from '@/views/map/MapPage.vue';
import DashboardPage from '@/views/map/dashboard/DashboardPage.vue';
import PermissionWizard from '@/views/permission-wizard/PermissionWizard.vue';
import { useUser } from '@/stores/user.store';
import PropertyDetails from '@/views/map/property/PropertyDetails.vue';
import NetworkPage from '@/views/map/network/NetworkPage.vue';
import GatewayDetailsPage from '@/views/map/gateway/GatewayDetailsPage.vue';
import RepeaterDetailsPage from '@/views/map/network/RepeaterDetailsPage.vue';

const AuthGuard: NavigationGuard = async (to) => {
  const session = useAuth();
  if (session.state === 'AUTHENTICATING' || session.state === 'INITIALIZING') {
    console.log('IN AUTH GUARD WAITING FOR STATE CHANGE', session.state);
    await session.waitForStateChange();
  }
  if (!['/login'].includes(to.path) && session.state !== 'AUTHENTICATED') {
    console.log('UNAUTHENTICATED - REDIRECTING TO LOGIN');
    return { path: '/login' };
  }
};

const SystemPermissionGuard: NavigationGuard = async (to) => {
  if (!['/login', '/permission-wizard'].includes(to.path)) {
    const user = useUser();
    const shouldRedirect = await user.shouldRedirectToPermissions();
    if (shouldRedirect) {
      return { path: '/permission-wizard' };
    }
  }
};

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    component: HomePage,
    name: 'root',
    children: [
      {
        path: '',
        component: MapPage,
        name: 'map',
        children: [
          {
            name: 'map.dashboard',
            path: '',
            component: DashboardPage,
          },
          {
            name: 'map.property',
            path: 'property/:propertyId',
            component: PropertyDetails,
          },
          {
            name: 'map.network',
            path: 'network/:propertyId',
            component: NetworkPage,
          },
          {
            name: 'map.gateway',
            path: 'gateway/:propertyId',
            component: GatewayDetailsPage,
          },
          {
            name: 'map.repeater',
            path: 'repeater/:propertyId/:repeaterId',
            component: RepeaterDetailsPage,
          },
        ],
      },
      {
        name: 'property-list',
        path: 'property-list',
        component: () => import('@/views/property-list/PropertiesPage.vue'),
      },
      {
        name: 'unit-list',
        path: 'units/:propertyId',
        component: () => import('@/views/unit-list/UnitsPage.vue'),
      },
      {
        name: 'unit-page',
        path: '/unit/:propertyId/:unitId',
        props: true,
        component: () => import('@/views/unit/UnitPage.vue'),
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('@/views/settings/SettingsPage.vue'),
      },
      {
        name: 'direct-connect',
        path: 'direct-connect',
        component: () => import('@/views/tools/direct-connect/DirectConnectPage.vue'),
      },
      {
        name: 'barcode-scanner',
        path: 'barcode-scanner',
        component: () => import('@/views/tools/BarcodeScannerPage.vue'),
      },
      {
        name: 'test-connect',
        path: 'test-connect',
        component: () => import('@/views/tools/test-connect/TestConnectPage.vue'),
      },
      {
        name: 'replace-device',
        path: 'replace-device',
        component: () => import('@/views/tools/replace-device/ReplaceDevicePage.vue'),
      },
      {
        name: 'warranty-lookup',
        path: 'warranty-lookup',
        component: () => import('@/views/tools/WarrantyLookupPage.vue'),
      },
      {
        name: 'contact-us',
        path: 'contact-us',
        component: () => import('@/views/SupportPage.vue'),
      },
      {
        name: 'masquerade',
        path: 'admin/masquerade',
        component: () => import('@/views/superadmin/MasqueradePage.vue'),
      },
    ],
  },
  {
    path: '/permission-wizard',
    name: 'permission-wizard',
    component: PermissionWizard,
  },
  {
    path: '/login',
    name: 'login',
    component: LoginPage,
  },
];

let router: Router;

export function InitRouter() {
  if (router) {
    return router;
  }
  router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
    scrollBehavior() {
      return { top: 0 };
    },
  });

  router.beforeEach(AuthGuard);
  router.beforeEach(SystemPermissionGuard);

  return router;
}
